import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronLeft from "@sunSource/Icons/ChevronLeft";
import ChevronRight from "@sunSource/Icons/ChevronRight";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: true,
    };
    const categoriesDataView = getCategoriesDataView(state, getCategoriesParameter);
    const shouldLoadBrandCategories = !categoriesDataView.isLoading && !categoriesDataView.value;

    return {
        categoriesDataView,
        getCategoriesParameter,
        shouldLoadBrandCategories,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
    imageItem?: InjectableCss;
    companyContentWrapper?: InjectableCss;
    companyTitle?: TypographyProps;
    companyDesc?: TypographyProps;
    infoCardTitle?: TypographyProps;
    companyImage?: InjectableCss;
    content?: InjectableCss;
    images?: InjectableCss;
    linkStyles?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;

    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
    },
    categoryItem: {
        // width is dynamic here
        width: 0,
    },
    innerContainer: {
        gap: 10,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 160px;
        `,
    },
    categoryImage: {
        width: "160px",
        height: "160px",
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
        `,
    },
    categoryName: {
        css: css`
            color: #363636;
            font-weight: bold;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: bold;
            padding-top: 10px;
        `,
    },
    wrapper: {
        css: css`
            margin: 0px auto 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 720px !important;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0px;
                            flex-wrap: nowrap;
                        `,
                        css`
                            max-width: 1300px !important;
                            flex-wrap: nowrap;
                            padding: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    infoCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-size: 2rem;
            margin: 30px 0 10px;
            color: ${getColor("primary.main")};
        `,
    },
    imageItem: {
        css: css`
            padding: 5px 25px;
            justify-content: center;
            display: flex;
        `,
    },
    companyContentWrapper: {
        css: css`
            margin-top: 35px;
            display: flex;
            padding: 30px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-wrap: wrap;
                        `,
                        null,
                        css`
                            flex-wrap: nowrap;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    companyTitle: {
        variant: "h3",
        css: css`
            text-transform: uppercase;
            font-size: 2rem;
            font-weight: 600;
            margin-bottom: 30px;
        `,
    },
    content: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 60%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    images: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 40%;
                        `,
                    ],
                    "min",
                )}
            display: flex;
            justify-content: center;
            align-items: center;
            > div:first-child {
                display: flex;
                flex-wrap: nowrap;
                flex-direction: column;
                width: 80%;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-top: 50px;
                            `,
                            null,
                            css`
                                margin-top: -50px;
                            `,
                        ],
                        "min",
                    )}
                > div[class^="LazyImageStyle-sc-"] {
                    overflow: initial;
                }
            }
            > div:first-child img {
                width: 90%;
            }
        `,
    },
    companyDesc: {
        css: css`
            line-height: 30px;
        `,
    },
    linkStyles: {
        css: css`
            border: 1px solid;
            width: 75%;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 9px 0;
            margin: 35px auto 0;
            text-transform: uppercase;
            font-weight: 600;
            background-color: #fff;
            border: 1px solid ${getColor("common.backgroundContrast")};
        `,
    },
    mainWrapper: {
        css: css`
            /* ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 5px 35px 5px 35px;
                        `,
                        null,
                        null,
                        css`
                            padding: 0px 95px 0px 95px;
                        `,
                    ],
                    "min",
                );
            }} */
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
            color: ${getColor("text.accent")};
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            border-radius: 50%;
            background: transparent;
            left: -9px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                width: 30px;
                height: 30px;
            }
            border: 0;
            padding: 0;
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            right: -9px;
            border-radius: 50%;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                //stroke: white;
                width: 30px;
                height: 30px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 0px 30px 20px;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            background: transparent;
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            border-radius: 12px;
            padding: 0 10px;
        `,
    },
    carouselSlideInner: {
        css: css`
            [class^="LazyImageStyle-sc-"] {
                min-height: 150px;
                max-height: 150px;
                height: 150px;
                background: #fff;
                margin-bottom: 10px;
            }
            width: 100%;
            height: 100%;
            position: relative;
            padding: 15px 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            [data-test-selector="productImage"] ~ div {
                margin: 15px 0 7px 0;
                [class^="TypographyStyle"] {
                    font-size: 14px;
                }
            }
            [class^="TypographyStyle"] {
                font-size: 15px;
                text-align: center;
                color: ${getColor("text.accent")} !important ;
                font-weight: 500;
                &:hover {
                    color: ${getColor("text.accent")};
                }
            }
        `,
    },
    carouselProductStyles: {
        productBrandStyles: {
            nameLink: {
                css: css`
                    [class^="TypographyStyle"] {
                        font-weight: 400 !important;
                    }
                `,
            },
        },
        productImageStyles: {
            linkWrappingImage: {
                css: css`
                    display: flex;
                    justify-content: center;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 150px;
                    width: 150px;
                `,
            },
        },
    },
};

const styles = categoryListStyles;

class CategoryList extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories, theme } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            theme,
            shellContext,
            fields: { showImages, showOnlyTopLevelCategories },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const category = categoriesDataView.value
            .filter(o => topLevelCategoryIds.indexOf(o.id) > -1)
            .find(cat => cat.name === "Our Products");

        if (!category) {
            return null;
        }

        return (
            <CustomCategoryProductCarousal
                ProductCategories={category}
                showImages={showImages}
                showOnlyTopLevelCategories={showOnlyTopLevelCategories}
                theme={theme}
                shellContext={shellContext}
            />
        );
    }
}

const CustomCategoryProductCarousal: React.FC<any> = ({
    ProductCategories,
    showImages,
    showOnlyTopLevelCategories,
    shellContext,
    theme,
}) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [5]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [5, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !ProductCategories.subCategoryIds.length) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: ProductCategories.subCategoryIds.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, ProductCategories.subCategoryIds.length]);

    if (!ProductCategories) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.mainContainer}>
                    <GridItem {...styles.prevArrowGridItem}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    </GridItem>

                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {ProductCategories.subCategoryIds &&
                                        ProductCategories.subCategoryIds.map((subCategoryId: any) => (
                                            <StyledWrapper
                                                key={subCategoryId}
                                                {...styles.carouselSlide}
                                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                                            >
                                                <StyledWrapper {...styles.carouselSlideInner}>
                                                    <SubCategoryLink categoryId={subCategoryId} />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>

                    <GridItem {...styles.nextArrowGridItem}>
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            <LazyImage src={category.smallImagePath} {...styles.imageItem} />
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(CategoryList))),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
